import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Laptop, Shield, Wifi, Clock, Users, Phone, Mail, MapPin, ChevronRight, Award, Building2, Terminal, Database, Cloud, ArrowRight, Bot, Code, Coins, Home, Wrench, Zap, Menu, X } from 'lucide-react';
import { AuthModal } from './components/AuthModal';
import { CustomerPortal } from './pages/CustomerPortal';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsOfService } from './pages/TermsOfService';
import { HomePage } from './pages/HomePage';
import { supabase } from './lib/supabase';

function App() {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md py-4' : 'bg-transparent py-6'}`}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <Terminal className={`w-8 h-8 ${scrolled ? 'text-mint-600' : 'text-white'}`} />
              <span className={`text-xl font-bold ${scrolled ? 'text-gray-900' : 'text-white'}`}>
                Las Vegas <span className="text-mint-600">Computer</span> <span style={{ color: '#FF6F61' }}>Help!</span>
              </span>
            </div>

            {/* Mobile menu button - Always visible on mobile */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="block md:hidden z-50"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className={`w-8 h-8 ${scrolled ? 'text-gray-900' : 'text-white'}`} />
              ) : (
                <Menu className={`w-8 h-8 ${scrolled ? 'text-gray-900' : 'text-white'}`} />
              )}
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a 
                href="#services" 
                className={`${scrolled ? 'text-gray-600' : 'text-white'} hover:text-mint-500 transition-colors`}
              >
                Services
              </a>
              <a 
                href="#about" 
                className={`${scrolled ? 'text-gray-600' : 'text-white'} hover:text-mint-500 transition-colors`}
              >
                About
              </a>
              <a 
                href="https://lvcomputerhelp.com/blog"
                target="_blank"
                rel="noopener noreferrer"
                className={`${scrolled ? 'text-gray-600' : 'text-white'} hover:text-mint-500 transition-colors`}
              >
                Blog
              </a>
              <a 
                href="#contact" 
                className={`${scrolled ? 'text-gray-600' : 'text-white'} hover:text-mint-500 transition-colors`}
              >
                Contact
              </a>
              <button 
                onClick={() => setIsAuthModalOpen(true)}
                className={`${scrolled ? 'text-gray-600' : 'text-white'} hover:text-mint-500 transition-colors`}
              >
                Customer Portal
              </button>
              <a 
                href="#contact"
                className="bg-mint-600 hover:bg-mint-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Book Service
              </a>
            </div>
          </div>

          {/* Mobile Navigation - Full screen overlay */}
          {mobileMenuOpen && (
            <div className="fixed inset-0 bg-white/50 backdrop-blur-md z-40 md:hidden">
              <div className="flex flex-col items-center justify-center h-full space-y-8 text-xl">
                <a 
                  href="#services"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-gray-800 hover:text-mint-600 transition-colors"
                >
                  Services
                </a>
                <a 
                  href="#about"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-gray-800 hover:text-mint-600 transition-colors"
                >
                  About
                </a>
                <a 
                  href="https://lvcomputerhelp.com/blog"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-800 hover:text-mint-600 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Blog
                </a>
                <a 
                  href="#contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-gray-800 hover:text-mint-600 transition-colors"
                >
                  Contact
                </a>
                <button 
                  onClick={() => {
                    setIsAuthModalOpen(true);
                    setMobileMenuOpen(false);
                  }}
                  className="text-gray-800 hover:text-mint-600 transition-colors"
                >
                  Customer Portal
                </button>
                <a 
                  href="#contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="bg-mint-600 hover:bg-mint-700 text-white px-8 py-3 rounded-lg font-medium transition-colors"
                >
                  Book Service
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/portal" element={<CustomerPortal />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
      </Routes>
    </div>
  );
}

export default App;